import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Play, Pause, Volume2, VolumeX, Download, Music, Disc3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { SymbolsTabs } from '../components/SymbolsTabs';

const THEME_SONG_URL = "https://customer-assets-4nw71qhi.emergentagent.net/job_87e159dc-3189-4727-ae1e-bf728f7f511e/artifacts/7pxiwec3_NDP%20Theam%20Song%20final.mp3";

const ThemeSong = () => {
  const { t } = useLanguage();
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => { if (audio.duration) setProgress((audio.currentTime / audio.duration) * 100); };
    const onLoaded = () => setDuration(audio.duration);
    const onEnded = () => { setIsPlaying(false); audio.currentTime = 0; };
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', onLoaded);
    audio.addEventListener('ended', onEnded);
    return () => { audio.removeEventListener('timeupdate', onTime); audio.removeEventListener('loadedmetadata', onLoaded); audio.removeEventListener('ended', onEnded); };
  }, []);

  const togglePlay = () => {
    if (isPlaying) { audioRef.current.pause(); } else { audioRef.current.play().catch(() => {}); }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    audioRef.current.currentTime = ((e.clientX - rect.left) / rect.width) * duration;
  };

  const formatTime = (s) => {
    if (!s || isNaN(s)) return '0:00';
    return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`;
  };

  const currentTime = audioRef.current?.currentTime || 0;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="theme-song-page">
      <audio ref={audioRef} src={THEME_SONG_URL} preload="metadata" />

      {/* Hero */}
      <section className="bg-gradient-to-br from-gray-900 via-party-red/90 to-gray-900 py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="absolute rounded-full bg-white" style={{ width: `${60 + i * 40}px`, height: `${60 + i * 40}px`, top: `${10 + i * 15}%`, left: `${5 + i * 16}%`, opacity: 0.1 + i * 0.02 }} />
          ))}
        </div>
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <div className="mb-8 flex justify-center"><SymbolsTabs dark /></div>
          <div className={`w-28 h-28 mx-auto mb-6 rounded-full bg-white/10 backdrop-blur flex items-center justify-center border border-white/20 ${isPlaying ? 'animate-spin-slow' : ''}`}>
            <Disc3 className="h-14 w-14 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">
            {t({ en: 'NDP Theme Song', bn: 'এনডিপি থিম সং' })}
          </h1>
          <p className="text-lg text-red-200 mb-8">
            {t({ en: 'The anthem of the National Democratic Party of Bangladesh', bn: 'ন্যাশনাল ডেমোক্রেটিক পার্টি অফ বাংলাদেশের গান' })}
          </p>

          {/* Player */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 max-w-2xl mx-auto">
            <div className="flex items-center justify-center gap-4 mb-4">
              <button onClick={() => { audioRef.current.muted = !isMuted; setIsMuted(!isMuted); }} className="text-white/60 hover:text-white transition-colors">
                {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
              </button>
              <button onClick={togglePlay} className="w-16 h-16 rounded-full bg-white text-party-red hover:scale-105 flex items-center justify-center transition-transform shadow-lg" data-testid="theme-page-play-btn">
                {isPlaying ? <Pause className="h-7 w-7" /> : <Play className="h-7 w-7 ml-1" />}
              </button>
              <a href={THEME_SONG_URL} download="NDP_Theme_Song.mp3" className="text-white/60 hover:text-party-green transition-colors" data-testid="theme-page-download-btn">
                <Download className="h-5 w-5" />
              </a>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-white/60 text-sm w-10 text-right">{formatTime(currentTime)}</span>
              <div className="flex-1 h-2 bg-white/20 rounded-full cursor-pointer group" onClick={handleSeek}>
                <div className="h-full bg-white rounded-full relative transition-all" style={{ width: `${progress}%` }}>
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full opacity-0 group-hover:opacity-100 shadow-lg transition-opacity" />
                </div>
              </div>
              <span className="text-white/60 text-sm w-10">{formatTime(duration)}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Lyrics */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <Music className="h-8 w-8 text-party-red mx-auto mb-3" />
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              {t({ en: 'Song Lyrics', bn: 'গানের কথা' })}
            </h2>
            <div className="w-20 h-1 bg-party-red mx-auto rounded-full"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Bengali Lyrics */}
            <Card className="overflow-hidden">
              <div className="bg-party-green text-white p-4 text-center">
                <h3 className="text-lg font-bold">বাংলা</h3>
              </div>
              <CardContent className="p-6">
                <div className="text-gray-700 leading-loose text-base whitespace-pre-line font-bengali" data-testid="lyrics-bengali">
{`এই দেশ আমার, এই মাটি আমার
এই আকাশ, এই বাতাস
ন্যাশনাল ডেমোক্রেটিক পার্টির গান
ভালোবাসার এই দেশ বাংলাদেশ

গণতন্ত্রের পথে এগিয়ে যাই
ঐক্যের বাঁধনে বাঁধা পড়ি
ন্যায়বিচার আর সমতার স্বপ্ন
সবার মনে জ্বালাই আলো

জাতি ধর্ম বর্ণ নির্বিশেষে
সকলের জন্য সমান অধিকার
শান্তি আর উন্নয়নের পথে
এনডিপি চলে অবিরাম

এসো সবাই হাতে হাত রেখে
গড়ে তুলি নতুন বাংলাদেশ
গণতন্ত্র আর স্বাধীনতার
পতাকা উড়াই আকাশে

ন্যাশনাল ডেমোক্রেটিক পার্টি
জনগণের দল, জনগণের পাশে
এগিয়ে চলো, এগিয়ে চলো
নতুন দিনের আশায়`}
                </div>
              </CardContent>
            </Card>

            {/* English Translation */}
            <Card className="overflow-hidden">
              <div className="bg-party-red text-white p-4 text-center">
                <h3 className="text-lg font-bold">English Translation</h3>
              </div>
              <CardContent className="p-6">
                <div className="text-gray-700 leading-loose text-base whitespace-pre-line" data-testid="lyrics-english">
{`This country is mine, this soil is mine
This sky, this air
The song of the National Democratic Party
This beloved land — Bangladesh

On the path of democracy we march forward
Bound together in the bond of unity
The dream of justice and equality
We light the flame in every heart

Regardless of nation, religion, or colour
Equal rights for all
On the road to peace and progress
NDP marches on, unceasing

Come, let us join hands together
And build a new Bangladesh
Raise the flag of democracy
And freedom high in the sky

National Democratic Party
The people's party, beside the people
March on, march on
With hope for a new dawn`}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Download CTA */}
          <div className="mt-12 text-center">
            <a href={THEME_SONG_URL} download="NDP_Theme_Song.mp3">
              <Button className="bg-party-red hover:bg-red-700 text-white px-8 py-3 text-lg" data-testid="theme-download-cta">
                <Download className="h-5 w-5 mr-2" />
                {t({ en: 'Download Theme Song', bn: 'থিম সং ডাউনলোড করুন' })}
              </Button>
            </a>
            <p className="text-gray-400 text-sm mt-3">
              {t({ en: 'MP3 Format — Free Download', bn: 'এমপিথ্রি ফরম্যাট — বিনামূল্যে ডাউনলোড' })}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ThemeSong;
